import { useEffect, useRef, useState } from "react";
import { useDataProvider, useGetList, useNotify } from "ra-core";
import {
  AlertTriangle,
  ArrowRight,
  ChevronDown,
  Loader2,
  Mail,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import { format, formatDistanceToNowStrict } from "date-fns";
import { fr } from "date-fns/locale";
import ReactMarkdown from "react-markdown";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { streamSkillRun } from "@/lib/agenticClient";
import { getSupabaseClient } from "../providers/supabase/supabase";

// Sprint 7 T7.1.b — Refonte visuelle conforme à la maquette
// design/atomic-crm/home-app.jsx (MorningBrief) + home.css
// (.morning-brief). Markdown rendu en HTML stylé via react-markdown
// pour transformer la sortie du skill morning_brief_ds (DeepSeek)
// en contenu lisible — fini le whitespace-pre-line sur du markdown brut.

const SKILL_ID = "morning_brief_ds";
const SKILL_MODEL = "deepseek-chat";

export type DailyBriefAudience = "commercial" | "dev";

export interface DailyBrief {
  id: string;
  user_id: string;
  brief_date: string;
  content: string;
  audience: DailyBriefAudience;
  model_used: string | null;
  generated_at: string;
  is_current: boolean;
  created_at: string;
}

type BriefState = "empty" | "pending" | "generated" | "error";

function todayIsoDate(): string {
  return format(new Date(), "yyyy-MM-dd");
}

const COLLAPSE_STORAGE_KEY = (audience: DailyBriefAudience) =>
  `brief-daily-banner-collapsed:${audience}`;

function readCollapsed(audience: DailyBriefAudience): boolean {
  if (typeof window === "undefined") return false;
  try {
    return localStorage.getItem(COLLAPSE_STORAGE_KEY(audience)) === "1";
  } catch {
    return false;
  }
}

export function BriefDailyBanner({
  audience,
}: {
  audience: DailyBriefAudience;
}) {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [authUserId, setAuthUserId] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(() =>
    readCollapsed(audience),
  );
  const abortRef = useRef<AbortController | null>(null);
  const today = todayIsoDate();

  useEffect(() => {
    try {
      localStorage.setItem(
        COLLAPSE_STORAGE_KEY(audience),
        collapsed ? "1" : "0",
      );
    } catch {
      // localStorage indisponible (incognito strict) — fail silently
    }
  }, [audience, collapsed]);

  useEffect(() => {
    let cancelled = false;
    getSupabaseClient()
      .auth.getUser()
      .then(({ data }) => {
        if (!cancelled) setAuthUserId(data.user?.id ?? null);
      })
      .catch(() => {
        if (!cancelled) setAuthUserId(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  const enabled = !!authUserId;
  const query = useGetList<DailyBrief>(
    "daily_briefs",
    {
      pagination: { page: 1, perPage: 1 },
      sort: { field: "generated_at", order: "DESC" },
      filter: {
        user_id: authUserId,
        brief_date: today,
        audience,
        is_current: true,
      },
    },
    { enabled },
  );

  const brief = query.data?.[0];
  const state: BriefState = pending
    ? "pending"
    : errorMessage
      ? "error"
      : brief
        ? "generated"
        : "empty";

  const handleGenerate = async () => {
    if (!authUserId) return;
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;

    setPending(true);
    setErrorMessage(null);
    let content: string | null = null;

    try {
      for await (const ev of streamSkillRun(
        SKILL_ID,
        { audience },
        { signal: ctrl.signal },
      )) {
        if (ev.event === "run.done") {
          const out = (ev.data as { output?: { markdown?: string } }).output;
          content = out?.markdown ?? null;
        } else if (ev.event === "run.error") {
          throw new Error(
            (ev.data as { error?: string }).error ?? "skill_run_failed",
          );
        }
      }
    } catch (err) {
      if (ctrl.signal.aborted) return;
      const msg = (err as Error).message;
      setErrorMessage(msg);
      notify(`Génération du brief échouée : ${msg}`, { type: "error" });
      setPending(false);
      return;
    }

    if (!content) {
      const msg = "Le brief généré est vide.";
      setErrorMessage(msg);
      notify(msg, { type: "warning" });
      setPending(false);
      return;
    }

    try {
      await dataProvider.create("daily_briefs", {
        data: {
          user_id: authUserId,
          brief_date: today,
          content,
          audience,
          model_used: SKILL_MODEL,
          is_current: true,
        },
      });
      await query.refetch();
    } catch (err) {
      notify(`Brief généré mais non sauvegardé : ${(err as Error).message}`, {
        type: "warning",
      });
    } finally {
      setPending(false);
    }
  };

  // Sprint 6 T6.1 — bouton "Envoyer par email" (inchangé).
  const handleSendEmail = async () => {
    setSending(true);
    try {
      const { data, error } = await getSupabaseClient().functions.invoke(
        "send_brief_to_self",
        { body: {} },
      );
      if (error) throw error;
      const result = (data as { sent?: boolean; recipient?: string }) ?? {};
      if (result.sent && result.recipient) {
        notify(`Brief envoyé à ${result.recipient}`, { type: "success" });
      } else {
        notify("Brief envoyé.", { type: "success" });
      }
    } catch (err) {
      const msg = (err as { message?: string }).message ?? String(err);
      notify(`Envoi par email échoué : ${msg}`, { type: "error" });
    } finally {
      setSending(false);
    }
  };

  if (!authUserId) return null;

  const audienceLabel =
    audience === "commercial"
      ? "Brief IA — ta journée commerciale"
      : "Brief IA — ta journée dev";

  // Maquette : fond pastel doux + border. Violet pour le brief du matin
  // (cohérent avec l'identité IA du produit).
  const containerBase =
    "rounded-xl border px-4 py-3.5 md:py-4 flex gap-3.5 md:gap-4 items-start";
  const containerVariant =
    state === "error"
      ? "bg-gradient-to-b from-red-50 to-red-100/60 border-red-200 dark:from-red-950/30 dark:to-red-900/20 dark:border-red-900/40"
      : "bg-gradient-to-b from-violet-50 to-violet-100/50 border-violet-200 dark:from-violet-950/30 dark:to-violet-900/20 dark:border-violet-900/40";

  const iconWrapBase =
    "size-8 shrink-0 rounded-lg flex items-center justify-center";
  const iconWrapVariant =
    state === "error"
      ? "bg-red-100 text-red-700 dark:bg-red-950/60 dark:text-red-300"
      : "bg-violet-100 text-violet-700 dark:bg-violet-950/60 dark:text-violet-300";

  const labelBase = "text-[11px] font-semibold uppercase tracking-wider mb-1.5";
  const labelVariant =
    state === "error"
      ? "text-red-800 dark:text-red-300"
      : "text-violet-800 dark:text-violet-300";

  return (
    <div
      className={cn(containerBase, containerVariant)}
      data-testid="brief-daily-banner"
    >
      <div className={cn(iconWrapBase, iconWrapVariant)}>
        {state === "error" ? (
          <AlertTriangle className="size-4" />
        ) : (
          <Sparkles className="size-4" />
        )}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className={cn(labelBase, labelVariant, "mb-0 flex-1 min-w-0")}>
            {state === "error" ? "Brief du matin · erreur" : "Brief du matin"}
            {state === "generated" && brief && collapsed && (
              <span className="ml-2 normal-case font-normal text-muted-foreground tracking-normal">
                · mis à jour{" "}
                {formatDistanceToNowStrict(new Date(brief.generated_at), {
                  addSuffix: true,
                  locale: fr,
                })}
              </span>
            )}
          </div>
          {state === "generated" && (
            <button
              type="button"
              onClick={() => setCollapsed((c) => !c)}
              aria-label={collapsed ? "Dérouler le brief" : "Réduire le brief"}
              aria-expanded={!collapsed}
              className="shrink-0 size-6 -mt-0.5 inline-flex items-center justify-center rounded text-muted-foreground hover:text-foreground hover:bg-violet-100/60 dark:hover:bg-violet-900/30"
            >
              <ChevronDown
                className={cn(
                  "size-4 transition-transform",
                  collapsed && "-rotate-90",
                )}
              />
            </button>
          )}
        </div>

        {state === "pending" && (
          <div className="mt-1.5">
            <PendingSkeleton />
          </div>
        )}

        {state === "empty" && (
          <div className="mt-1.5">
            <EmptyCta
              audienceLabel={audienceLabel}
              onGenerate={handleGenerate}
              disabled={!authUserId}
            />
          </div>
        )}

        {state === "error" && (
          <div className="mt-1.5">
            <ErrorBody message={errorMessage!} onRetry={handleGenerate} />
          </div>
        )}

        {state === "generated" && brief && !collapsed && (
          <div className="mt-1.5">
            <GeneratedBody
              brief={brief}
              sending={sending}
              onRegenerate={handleGenerate}
              onSendEmail={handleSendEmail}
            />
          </div>
        )}
      </div>
    </div>
  );
}

function PendingSkeleton() {
  return (
    <div className="flex flex-col gap-1.5 mt-1">
      <div className="h-3 w-[94%] rounded bg-violet-200/60 dark:bg-violet-800/40 animate-pulse" />
      <div className="h-3 w-[82%] rounded bg-violet-200/60 dark:bg-violet-800/40 animate-pulse" />
      <div className="h-3 w-[60%] rounded bg-violet-200/60 dark:bg-violet-800/40 animate-pulse" />
    </div>
  );
}

function EmptyCta({
  audienceLabel,
  onGenerate,
  disabled,
}: {
  audienceLabel: string;
  onGenerate: () => void;
  disabled: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-semibold text-foreground">
          {audienceLabel}
        </p>
        <p className="text-[12.5px] text-muted-foreground mt-0.5">
          Aucun brief pour aujourd'hui. Génère-en un pour cadrer ta journée.
        </p>
      </div>
      <Button
        type="button"
        size="sm"
        onClick={onGenerate}
        disabled={disabled}
        className="gap-1.5"
      >
        <Sparkles className="size-3.5" />
        Générer le brief
        <ArrowRight className="size-3.5" />
      </Button>
    </div>
  );
}

function ErrorBody({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <>
      <p className="text-[13px] text-foreground/90 leading-relaxed">
        Impossible de générer le brief : <strong>{message}</strong>
      </p>
      <div className="mt-2.5 flex items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="gap-1.5 h-7 px-2.5 text-xs"
          onClick={onRetry}
        >
          <RefreshCw className="size-3" />
          Réessayer
        </Button>
      </div>
    </>
  );
}

function GeneratedBody({
  brief,
  sending,
  onRegenerate,
  onSendEmail,
}: {
  brief: DailyBrief;
  sending: boolean;
  onRegenerate: () => void;
  onSendEmail: () => void;
}) {
  const updatedAt = formatDistanceToNowStrict(new Date(brief.generated_at), {
    addSuffix: true,
    locale: fr,
  });

  return (
    <>
      <div className="brief-markdown text-[13.5px] text-foreground/90 leading-relaxed">
        <ReactMarkdown
          components={{
            h1: ({ children }) => (
              <h3 className="text-[14px] font-semibold text-foreground mt-2.5 mb-1 first:mt-0">
                {children}
              </h3>
            ),
            h2: ({ children }) => (
              <h3 className="text-[14px] font-semibold text-foreground mt-2.5 mb-1 first:mt-0">
                {children}
              </h3>
            ),
            h3: ({ children }) => (
              <h4 className="text-[13px] font-semibold text-foreground mt-2 mb-0.5 first:mt-0">
                {children}
              </h4>
            ),
            p: ({ children }) => (
              <p className="my-1 first:mt-0 last:mb-0">{children}</p>
            ),
            ul: ({ children }) => (
              <ul className="my-1 space-y-0.5 list-disc pl-5 marker:text-violet-500/70">
                {children}
              </ul>
            ),
            ol: ({ children }) => (
              <ol className="my-1 space-y-0.5 list-decimal pl-5">{children}</ol>
            ),
            li: ({ children }) => <li className="pl-0.5">{children}</li>,
            strong: ({ children }) => (
              <strong className="font-semibold text-foreground">
                {children}
              </strong>
            ),
            a: ({ href, children }) => (
              <a
                href={href}
                className="text-violet-700 hover:underline dark:text-violet-300"
              >
                {children}
              </a>
            ),
            code: ({ children }) => (
              <code className="rounded bg-violet-100/70 dark:bg-violet-900/40 px-1 py-0.5 text-[12px] font-mono">
                {children}
              </code>
            ),
          }}
        >
          {brief.content}
        </ReactMarkdown>
      </div>
      <div className="mt-2.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-muted-foreground">
        <span>Mis à jour {updatedAt}</span>
        <span aria-hidden className="text-muted-foreground/50">
          ·
        </span>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="gap-1.5 h-7 px-2 text-xs"
          onClick={onRegenerate}
        >
          <RefreshCw className="size-3" />
          Régénérer
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="gap-1.5 h-7 px-2 text-xs"
          onClick={onSendEmail}
          disabled={sending}
        >
          {sending ? (
            <Loader2 className="size-3 animate-spin" />
          ) : (
            <Mail className="size-3" />
          )}
          {sending ? "Envoi…" : "Envoyer par email"}
        </Button>
      </div>
    </>
  );
}
